import { useState, useEffect } from "react";
import {
  ListChecks,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Lightbulb,
  ArrowRight,
  Circle,
  ShieldCheck,
  MapPin,
  Phone,
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { VIRTUAL_ADDRESS_PROVIDERS, BUSINESS_PHONE_PROVIDERS } from "../../data/chapters";

const YES = "yes";
const NO = "no";

function YesNoToggle({ question, value, onChange, testIdBase }) {
  return (
    <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
      <p className="mb-3 font-heading text-base font-bold text-[var(--c-text)]">{question}</p>
      <div className="flex gap-3">
        <button
          data-testid={`${testIdBase}-yes`}
          onClick={() => onChange(YES)}
          className={`flex flex-1 items-center justify-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-bold transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)] ${
            value === YES
              ? "border-[#A3E6D5] bg-[#A3E6D5]/20 text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)]"
              : "border-[var(--c-border-light)] bg-[var(--c-bg)] text-[var(--c-muted)]"
          }`}
        >
          <CheckCircle2 className="h-4 w-4" />
          Yes
        </button>
        <button
          data-testid={`${testIdBase}-no`}
          onClick={() => onChange(NO)}
          className={`flex flex-1 items-center justify-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-bold transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)] ${
            value === NO
              ? "border-[#1E3A8A] bg-[#1E3A8A]/10 text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)]"
              : "border-[var(--c-border-light)] bg-[var(--c-bg)] text-[var(--c-muted)]"
          }`}
        >
          <XCircle className="h-4 w-4" />
          No
        </button>
      </div>
    </div>
  );
}

function ProviderCards({ providers, testIdPrefix }) {
  return (
    <div className="space-y-2">
      {providers.map((p) => (
        <a
          key={p.name}
          data-testid={`${testIdPrefix}-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-3 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-3 transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_var(--c-shadow)]"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-heading text-sm font-bold text-[var(--c-text)]">{p.name}</p>
              <span className="rounded-full border border-[#A3E6D5] bg-[#A3E6D5]/15 px-2 py-0.5 text-[10px] font-bold text-[var(--c-text)]">{p.price}</span>
            </div>
            <p className="mt-0.5 truncate text-xs text-[var(--c-muted)]">{p.best_for}</p>
          </div>
          <ExternalLink className="h-4 w-4 flex-shrink-0 text-[#1E3A8A]" />
        </a>
      ))}
    </div>
  );
}

export default function StepZeroView({
  chapter,
  completedSteps,
  checklistStart,
  onToggle,
  answers,
  onAnswerChange,
}) {
  // Hydrate local state from saved answers
  const [hasAddress, setHasAddress] = useState(answers?.has_address || null);
  const [hasPhone, setHasPhone] = useState(answers?.has_phone || null);

  useEffect(() => {
    setHasAddress(answers?.has_address || null);
    setHasPhone(answers?.has_phone || null);
  }, [answers]);

  const updateAddress = (val) => {
    setHasAddress(val);
    onAnswerChange({ has_address: val, has_phone: hasPhone });
  };
  const updatePhone = (val) => {
    setHasPhone(val);
    onAnswerChange({ has_address: hasAddress, has_phone: val });
  };

  const addressChecked = completedSteps.includes(checklistStart);
  const phoneChecked = completedSteps.includes(checklistStart + 1);
  const allReady = addressChecked && phoneChecked;

  return (
    <div data-testid="step-zero-view" className="space-y-8">
      {/* Header */}
      <div>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[var(--c-border)] bg-[#1E3A8A] shadow-[3px_3px_0px_var(--c-shadow)]">
            <ListChecks className="h-6 w-6 text-white" />
          </div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
            Prerequisites
          </span>
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[var(--c-text)] sm:text-3xl lg:text-4xl">
          {chapter.title}
        </h2>
        <p className="mt-2 text-base text-[var(--c-muted)] sm:text-lg">{chapter.subtitle}</p>
      </div>

      {/* Intro */}
      <p className="text-base leading-relaxed text-[var(--c-text)]">{chapter.content.intro}</p>

      {/* Why this matters */}
      <div>
        <h3 className="font-heading text-xl font-semibold text-[var(--c-text)] sm:text-2xl">Why this matters</h3>
        <ul className="mt-3 space-y-2">
          {chapter.content.sections[0].items.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <Circle className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 fill-[#1E3A8A] text-[#1E3A8A]" />
              <span className="text-[var(--c-text)]">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Pro Tip */}
      <div className="rounded-xl border-2 border-[var(--c-border)] bg-[#A3E6D5]/15 p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
        <div className="flex items-start gap-3">
          <Lightbulb className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1E3A8A]" />
          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-wider text-[var(--c-text)]">Pro Tip</p>
            <p className="mt-1 text-sm leading-relaxed text-[var(--c-text)]">{chapter.content.proTip}</p>
          </div>
        </div>
      </div>

      {/* Decision Tree — Question 1 */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#1E3A8A]" />
          <h3 className="font-heading text-lg font-semibold text-[var(--c-text)]">Question 1 — Business Address</h3>
        </div>
        <YesNoToggle
          question="Do you have a business mailing address?"
          value={hasAddress}
          onChange={updateAddress}
          testIdBase="q-address"
        />
        {hasAddress === NO && (
          <div data-testid="address-providers" className="space-y-2 rounded-xl border-2 border-dashed border-[var(--c-border-medium)] bg-[var(--c-bg)] p-4">
            <p className="text-sm font-bold text-[var(--c-text)]">No problem — try one of these virtual address providers:</p>
            <ProviderCards providers={VIRTUAL_ADDRESS_PROVIDERS} testIdPrefix="address-provider" />
            <p className="text-xs text-[var(--c-muted)]">
              Once you've signed up and confirmed your address, come back and check the box below.
            </p>
          </div>
        )}
        {hasAddress === YES && (
          <div className="rounded-xl border-2 border-[#A3E6D5] bg-[#A3E6D5]/10 p-3 text-sm text-[var(--c-text)]">
            <CheckCircle2 className="mr-1.5 inline h-4 w-4 text-[#1E3A8A]" />
            Great — make sure to use that address (not your home) on all LLC paperwork.
          </div>
        )}
      </div>

      {/* Decision Tree — Question 2 */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Phone className="h-5 w-5 text-[#1E3A8A]" />
          <h3 className="font-heading text-lg font-semibold text-[var(--c-text)]">Question 2 — Business Phone</h3>
        </div>
        <YesNoToggle
          question="Do you have a business phone number?"
          value={hasPhone}
          onChange={updatePhone}
          testIdBase="q-phone"
        />
        {hasPhone === NO && (
          <div data-testid="phone-providers" className="space-y-2 rounded-xl border-2 border-dashed border-[var(--c-border-medium)] bg-[var(--c-bg)] p-4">
            <p className="text-sm font-bold text-[var(--c-text)]">No problem — try one of these business phone providers:</p>
            <ProviderCards providers={BUSINESS_PHONE_PROVIDERS} testIdPrefix="phone-provider" />
            <p className="text-xs text-[var(--c-muted)]">
              Once you've signed up and have your number, come back and check the box below.
            </p>
          </div>
        )}
        {hasPhone === YES && (
          <div className="rounded-xl border-2 border-[#A3E6D5] bg-[#A3E6D5]/10 p-3 text-sm text-[var(--c-text)]">
            <CheckCircle2 className="mr-1.5 inline h-4 w-4 text-[#1E3A8A]" />
            Great — keep this number separate from your personal cell.
          </div>
        )}
      </div>

      {/* Verification checklist */}
      <div>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#1E3A8A]" />
          <h3 className="font-heading text-lg font-semibold text-[var(--c-text)]">Verification — Mark these complete to unlock Step 1</h3>
        </div>
        <div className="mt-3 space-y-3">
          {chapter.checklistItems.map((item, ci) => {
            const idx = checklistStart + ci;
            const checked = completedSteps.includes(idx);
            return (
              <label
                key={`${chapter.id}-item-${ci}`}
                data-testid={`checklist-item-${chapter.id}-${ci}`}
                className="flex cursor-pointer items-start gap-3 rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-checklist-bg)] p-4 transition-all hover:border-[var(--c-border-medium)]"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggle(idx)}
                  className="mt-0.5 h-6 w-6 rounded-md border-2 border-[var(--c-border)] data-[state=checked]:bg-[#A3E6D5] data-[state=checked]:text-[#1A1918]"
                />
                <span className={`text-sm font-medium transition-all ${checked ? "text-[var(--c-muted)] line-through" : "text-[var(--c-text)]"}`}>
                  {item}
                </span>
              </label>
            );
          })}
        </div>
        {allReady && (
          <div data-testid="step-zero-ready" className="mt-4 flex items-center gap-2 rounded-xl border-2 border-[#A3E6D5] bg-[#A3E6D5]/15 p-4 shadow-[3px_3px_0px_var(--c-shadow)]">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#1E3A8A]" />
            <p className="text-sm font-bold text-[var(--c-text)]">
              You're ready! Click <strong>Next</strong> below to start Step 1 and form your LLC.
            </p>
            <ArrowRight className="ml-auto h-5 w-5 text-[#1E3A8A]" />
          </div>
        )}
      </div>
    </div>
  );
}
